/**
 * The peon's wardrobe (docs/peon-look.md): what a peon can wear, what it costs, and how the
 * pieces stack. Owner, 2026-10-04: "Make peon hairstyles and accessories that are purchasable
 * with gold" and "Make a proper character editor rather than the existing screen, it's getting
 * too crowded so it should have multiple tabs for accessories, hairstyles, facial hair, etc."
 *
 * Shirts stay free (the first customisation, 2026-09-24). Hair, facial hair and accessories are
 * bought once with gold and then worn whenever; "none" costs nothing. Prices run from a quick
 * chore's gold to a reward's (100 to 750; owner, 2026-10-04: "The cosmetics should be much more
 * expensive" than the first cut's 20 to 150), so a look is something a peon works toward. A peon owns whatever they
 * were already wearing when the shop opened. While the `shopUI` flag is off nothing costs
 * anything: a worn piece is simply granted, so the household can try the editor first.
 *
 * Pure and dependency-free: the server prices and enforces, the clients draw and ask.
 */
import { HAIRSTYLES, type Hairstyle } from "./shirts.ts";

export type CosmeticCategory = "hair" | "beard" | "accessory";
/** An accessory's place: one per slot may be worn at a time. */
export type AccessorySlot = "face" | "ears" | "neck";

export const BEARDS = ["none", "stubble", "moustache", "goatee", "fullbeard", "braidedbeard", "muttonchops"] as const;
export type Beard = (typeof BEARDS)[number];
export const DEFAULT_BEARD: Beard = "none";

export const ACCESSORIES = ["eyepatch", "monocle", "glasses", "earring", "hoops", "scarf", "necklace", "warpaint"] as const;
export type Accessory = (typeof ACCESSORIES)[number];

export interface Cosmetic {
  id: string;
  category: CosmeticCategory;
  label: string;
  /** Gold, once. 0 is free ("none" and the shirt are never listed here). */
  price: number;
  slot?: AccessorySlot;
  /** Whether a `-crowned` art twin exists (the piece reaches into the crown); else the plain file is drawn under the crown too. */
  crowned: boolean;
}

/** The art file of a piece, 1x (the clients take the @3x twin by the same rule as the shirt art): `/ui/<category>-<id>.png`. */
export function cosmeticArt(item: Pick<Cosmetic, "id" | "category" | "crowned">, crowned: boolean): string {
  return `/ui/${item.category}-${item.id}${crowned && item.crowned ? "-crowned" : ""}.png`;
}

const HAIR_PRICES: Record<Exclude<Hairstyle, "none">, number> = {
  mohawk: 300, topknot: 250, ponytail: 250, braid: 300, spiky: 300, mullet: 200,
  long: 400, bob: 300, pigtails: 350, buns: 400, curls: 600, twinbraids: 500,
  frizzmullet: 450,
};
const HAIR_LABELS_SHOP: Record<Exclude<Hairstyle, "none">, string> = {
  mohawk: "Mohawk", topknot: "Topknot", ponytail: "Ponytail", braid: "Braid", spiky: "Spiky", mullet: "Mullet",
  long: "Long", bob: "Bob", pigtails: "Pigtails", buns: "Buns", curls: "Curls", twinbraids: "Twin braids",
  frizzmullet: "Bossy Jossy",
};

/** Every piece that can be bought, in the order the editor shows them. */
export const COSMETICS: readonly Cosmetic[] = [
  ...HAIRSTYLES.filter((h): h is Exclude<Hairstyle, "none"> => h !== "none").map((id) => ({ id, category: "hair" as const, label: HAIR_LABELS_SHOP[id], price: HAIR_PRICES[id], crowned: true })),
  { id: "stubble", category: "beard", label: "Stubble", price: 100, crowned: false },
  { id: "moustache", category: "beard", label: "Moustache", price: 200, crowned: false },
  { id: "goatee", category: "beard", label: "Goatee", price: 250, crowned: false },
  { id: "fullbeard", category: "beard", label: "Full beard", price: 400, crowned: false },
  { id: "braidedbeard", category: "beard", label: "Braided beard", price: 500, crowned: false },
  { id: "muttonchops", category: "beard", label: "Mutton chops", price: 300, crowned: false },
  { id: "eyepatch", category: "accessory", label: "Eyepatch", price: 600, slot: "face", crowned: false },
  { id: "monocle", category: "accessory", label: "Monocle", price: 750, slot: "face", crowned: false },
  { id: "glasses", category: "accessory", label: "Glasses", price: 500, slot: "face", crowned: false },
  { id: "warpaint", category: "accessory", label: "War paint", price: 250, slot: "face", crowned: false },
  { id: "earring", category: "accessory", label: "Earring", price: 300, slot: "ears", crowned: false },
  { id: "hoops", category: "accessory", label: "Hoops", price: 450, slot: "ears", crowned: false },
  { id: "scarf", category: "accessory", label: "Scarf", price: 400, slot: "neck", crowned: false },
  { id: "necklace", category: "accessory", label: "Tusk necklace", price: 550, slot: "neck", crowned: false },
];

/** A piece by its id (ids are unique across categories), or undefined. */
export function cosmetic(id: string): Cosmetic | undefined {
  return COSMETICS.find((c) => c.id === id);
}

export function cosmeticsOf(category: CosmeticCategory): Cosmetic[] {
  return COSMETICS.filter((c) => c.category === category);
}

export function isBeard(v: unknown): v is Beard {
  return typeof v === "string" && (BEARDS as readonly string[]).includes(v);
}

export function isAccessory(v: unknown): v is Accessory {
  return typeof v === "string" && (ACCESSORIES as readonly string[]).includes(v);
}

/** The accessories worn at once: unique, known, at most one per slot (the later one in the list wins its slot). */
export function normalizeAccessories(worn: ReadonlyArray<unknown>): Accessory[] {
  const bySlot = new Map<AccessorySlot, Accessory>();
  for (const v of worn) {
    if (!isAccessory(v)) continue;
    const slot = cosmetic(v)?.slot;
    if (slot) bySlot.set(slot, v);
  }
  return ACCESSORIES.filter((a) => [...bySlot.values()].includes(a));
}

/** Putting `item` on: it takes its slot from whatever was there. */
export function wearAccessory(worn: ReadonlyArray<Accessory>, item: Accessory): Accessory[] {
  return normalizeAccessories([...worn.filter((w) => cosmetic(w)?.slot !== cosmetic(item)?.slot), item]);
}

export function removeAccessory(worn: ReadonlyArray<Accessory>, item: Accessory): Accessory[] {
  return worn.filter((w) => w !== item);
}

/** The pieces a look wears that must be owned (never "none", never the shirt). */
export function piecesWorn(look: { hair: string; beard: string; accessories: ReadonlyArray<string> }): string[] {
  return [look.hair, look.beard, ...look.accessories].filter((id) => id !== "none" && cosmetic(id) !== undefined);
}

/** Whether `balance` covers `price`; the shortfall for the "Need 40 more" line. */
export function shortBy(price: number, balance: number): number {
  return Math.max(0, price - balance);
}

/** The ledger's words for a purchase ("Mohawk", "Eyepatch"): the label, for the Recent list and the detail JSON. */
export function purchaseTitle(item: Cosmetic): string {
  return item.label;
}

/** A tab of the character editor: the shirt or a wardrobe category. */
export type EditorTabKey = "shirt" | CosmeticCategory;

/**
 * The editor's tabs, in order, with their words (owner: tabs for accessories, hairstyles, facial
 * hair, etc.). Four is the strip's limit at 375px (owner, 2026-10-05, of a fifth: "This looks like
 * ass"): the badges are their own section under the wardrobe, not a tab (docs/peon-look.md "Badges").
 */
export const EDITOR_TABS: ReadonlyArray<{ key: EditorTabKey; label: string }> = [
  { key: "shirt", label: "Shirt" },
  { key: "hair", label: "Hair" },
  { key: "beard", label: "Beard" },
  { key: "accessory", label: "Extras" },
];

/**
 * The draw order on a portrait, back to front: the shirt's portrait, the beard (under the hair at
 * the sideburns), the hair, then the accessories, neck before ears before face.
 */
export const DRAW_ORDER: readonly AccessorySlot[] = ["neck", "ears", "face"];
